export interface HelplineEmail {
  name: string;
  description: string;
  email: string;
}
export type DocumentCapabilityKey = 'create' | 'download' | 'share' | 'review' | 'esign' | 'library';
export interface FormData {
  submitterName: string;
  submitterEmail: string;
  groupDescription: string;
  needSubgroups: boolean | null;
  subgroupLevels: number | null;
  numberOfUsers: number | '';
  documentTemplateTagIds: string[];
  documentAllTags: boolean;
  documentServiceIds: string[];
  documentAllServices: boolean;
  documentCapabilities: DocumentCapabilityKey[];
  docsPerMonth: number | '';
  docsInProgress: number | '';
  maxPagesPerReview: number | '';
  monthlyShares: number | '';
  monthlyDownloads: number | '';
  monthlyReviews: number | '';
  hasLegalAICopilot: boolean | null;
  copilotProvinceIds: string[];
  copilotAllProvinces: boolean;
  copilotPracticeAreaIds: string[];
  copilotAllPracticeAreas: boolean;
  copilotMonthlyLimit: number | '';
  chatHistory: boolean;
  continuePreviousConversations: boolean;
  hasHelpline: boolean | null;
  hasAccessToIntakeForm: boolean | null;
  helplineMaskNumber: string;
  helplineRealNumber: string;
  helplinePrimaryEmail: string;
  helplineEmails: HelplineEmail[];
  hasGetAQuote: boolean | null;
  hasLegalInquiryForm: boolean;
  marketplaceLawyerIds: string[];
  marketplaceDiscount: number | '';
  claimsSubmit: boolean | null;
  claimsEdit: boolean | null;
  claimsChangeStatus: boolean | null;
  claimsLawyerIds: string[];
  hasKnowledgeCenter: boolean | null;
  knowledgeCenterCategoriesInput: string;
  supportAccess: boolean | null;
  supportCategoriesInput: string;
  onlyGroupAdminsCanSubmitTicket: boolean;
  skipPhoneVerification: boolean;
  canUseForgotPassword: boolean;
  viewSubscriptionPlans: boolean;
  showBillingInformation: boolean;
  otherNotes: string;
}
export interface StepProps {
  formData: FormData;
  updateFormData: (updates: Partial<FormData>) => void;
  errors: Record<string, string>;
  goToStep?: (step: number) => void;
}
export function createInitialFormData(submitter?: {
  name: string;
  email: string;
} | null): FormData {
  return {
    submitterName: submitter?.name ?? '',
    submitterEmail: submitter?.email ?? '',
    groupDescription: '',
    needSubgroups: null,
    subgroupLevels: null,
    numberOfUsers: '',
    documentTemplateTagIds: [],
    documentAllTags: false,
    documentServiceIds: [],
    documentAllServices: false,
    documentCapabilities: [],
    docsPerMonth: '',
    docsInProgress: '',
    maxPagesPerReview: '',
    monthlyShares: '',
    monthlyDownloads: '',
    monthlyReviews: '',
    hasLegalAICopilot: null,
    copilotProvinceIds: [],
    copilotAllProvinces: false,
    copilotPracticeAreaIds: [],
    copilotAllPracticeAreas: false,
    copilotMonthlyLimit: '',
    chatHistory: false,
    continuePreviousConversations: false,
    hasHelpline: null,
    hasAccessToIntakeForm: null,
    helplineMaskNumber: '',
    helplineRealNumber: '',
    helplinePrimaryEmail: '',
    helplineEmails: [],
    hasGetAQuote: null,
    hasLegalInquiryForm: false,
    marketplaceLawyerIds: [],
    marketplaceDiscount: '',
    claimsSubmit: null,
    claimsEdit: null,
    claimsChangeStatus: null,
    claimsLawyerIds: [],
    hasKnowledgeCenter: null,
    knowledgeCenterCategoriesInput: '',
    supportAccess: null,
    supportCategoriesInput: '',
    onlyGroupAdminsCanSubmitTicket: false,
    skipPhoneVerification: false,
    canUseForgotPassword: true,
    viewSubscriptionPlans: false,
    showBillingInformation: false,
    otherNotes: ''
  };
}
function joinList(values: string[]): string {
  return values.filter(Boolean).join(', ');
}
function parseCommaList(value: string): string[] {
  return value.split(',').map(s => s.trim()).filter(Boolean);
}

/** Migrate drafts saved with the older form shape. */
export function normalizeDraftFormData(raw: Record<string, unknown>): FormData {
  const base = createInitialFormData();
  const merged = {
    ...base,
    ...raw
  } as FormData & Record<string, unknown>;
  if (Array.isArray(raw.documentPackages) && !raw.documentTemplateTagIds) {
    merged.documentTemplateTagIds = [];
  }
  if (Array.isArray(raw.documentCategories) && !raw.documentServiceIds) {
    merged.copilotPracticeAreaIds = (raw.documentCategories as string[]).map(String);
  }
  if (Array.isArray(raw.copilotProvinces)) {
    merged.copilotProvinceIds = (raw.copilotProvinces as string[]).map(String);
  }
  if (Array.isArray(raw.copilotPracticeAreas)) {
    merged.copilotPracticeAreaIds = (raw.copilotPracticeAreas as string[]).map(String);
  }
  if (typeof raw.hasClaimsAccess === 'boolean' && raw.claimsSubmit === undefined) {
    merged.claimsSubmit = raw.hasClaimsAccess as boolean;
  }
  if (typeof raw.marketplaceLawyers === 'string' && raw.marketplaceLawyerIds === undefined) {
    merged.marketplaceLawyerIds = [];
  }
  if (Array.isArray(raw.knowledgeCenterCategories)) {
    const legacy = [...(raw.knowledgeCenterCategories as string[]), ...(raw.knowledgeCenterCustomCategories as string[] ?? [])];
    if (!raw.knowledgeCenterCategoriesInput) {
      merged.knowledgeCenterCategoriesInput = joinList(legacy);
    }
  }
  if (Array.isArray(raw.supportCategoryOverride) && !raw.supportCategoriesInput) {
    merged.supportCategoriesInput = joinList(raw.supportCategoryOverride as string[]);
  }
  const caps = raw.documentCapabilities;
  if (Array.isArray(caps) && caps.length && typeof caps[0] === 'string') {
    const map: Record<string, DocumentCapabilityKey> = {
      'Create documents': 'create',
      'Download documents': 'download',
      'Share documents': 'share',
      'Request legal document review': 'review',
      'E-signature': 'esign'
    };
    merged.documentCapabilities = (caps as string[]).map(c => map[c]).filter(Boolean) as DocumentCapabilityKey[];
  }
  return merged as FormData;
}